import {
  AttachmentIcon,
  CheckCircleIcon,
  ExternalLinkIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Heading,
  HStack,
  Icon,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useDropdownOptions } from "../../../hooks/useDropdownOptions";

const ItemConfirmationStep = ({ formData, isEditMode }) => {
  const { options: authGroupOptions } = useDropdownOptions("AUTHGROUPS");

  if (!formData) return null;

  /* ------------------ Get auth group label ------------------ */

  const getAuthGroupLabel = (value) => {
    const option = authGroupOptions.find((o) => o.value === value);
    return option ? option.label : value;
  };

  /* ------------------ Type-specific Config ------------------ */

  const getTypeConfig = (type) => {
    const configs = {
      file: { color: "blue", icon: AttachmentIcon },
      powerbi: { color: "yellow", icon: ExternalLinkIcon },
      tableau: { color: "orange", icon: ExternalLinkIcon },
      directory: { color: "purple", icon: InfoIcon },
      static: { color: "gray", icon: InfoIcon },
    };
    return configs[type] || configs.static;
  };

  const typeConfig = getTypeConfig(formData.type);

  return (
    <VStack align="stretch" spacing={5}>
      {/* Gradient Header */}
      <Box
        bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        borderRadius="xl"
        p={5}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="-20px"
          right="-20px"
          width="150px"
          height="150px"
          borderRadius="full"
          bg="whiteAlpha.200"
        />
        <Box
          position="absolute"
          bottom="-30px"
          left="-30px"
          width="120px"
          height="120px"
          borderRadius="full"
          bg="whiteAlpha.100"
        />

        <HStack spacing={3} position="relative">
          <Icon as={CheckCircleIcon} boxSize={5} color="white" />
          <Box>
            <Heading size="sm" color="white" mb={0.5}>
              Review Your {isEditMode ? "Changes" : "New Item"}
            </Heading>
            <Text color="whiteAlpha.900" fontSize="xs">
              Please verify all details before{" "}
              {isEditMode ? "updating" : "creating"} this item
            </Text>
          </Box>
        </HStack>
      </Box>

      {/* Main Content Card */}
      <Box
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        overflow="hidden"
      >
        {/* Title Section - Full Width */}
        <Box p={4} borderBottom="1px solid" borderColor="gray.100">
          <HStack spacing={2} mb={1}>
            <Box w="3px" h="14px" bg="teal.500" borderRadius="full" />
            <Text
              fontSize="xs"
              fontWeight="bold"
              color="gray.500"
              textTransform="uppercase"
            >
              Title
            </Text>
          </HStack>
          <Text fontSize="lg" fontWeight="semibold" color="gray.900">
            {formData.title}
          </Text>
        </Box>

        {/* Two Column Layout */}
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
          borderBottom="1px solid"
          borderColor="gray.100"
        >
          {/* Left Column */}
          <Box
            p={4}
            borderRight={{ base: "none", md: "1px solid" }}
            borderColor="gray.100"
          >
            <VStack align="stretch" spacing={3}>
              {/* Subtitle */}
              {formData.subTitle && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    mb={1}
                  >
                    SUBTITLE
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    {formData.subTitle}
                  </Text>
                </Box>
              )}

              {/* Type */}
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  mb={1.5}
                >
                  TYPE
                </Text>
                <Badge
                  colorScheme={typeConfig.color}
                  fontSize="sm"
                  px={2.5}
                  py={1}
                  borderRadius="md"
                  textTransform="capitalize"
                  display="inline-flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <Icon as={typeConfig.icon} boxSize={3.5} />
                  {formData.type}
                </Badge>
              </Box>
            </VStack>
          </Box>

          {/* Right Column */}
          <Box p={4}>
            <VStack align="stretch" spacing={3}>
              {/* Description or Auth Group */}
              {formData.desc && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    mb={1}
                  >
                    DESCRIPTION
                  </Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                    {formData.desc}
                  </Text>
                </Box>
              )}

              {/* Auth Group */}
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  mb={1.5}
                >
                  AUTHORIZATION
                </Text>
                <Badge
                  colorScheme="purple"
                  fontSize="sm"
                  px={2.5}
                  py={1}
                  borderRadius="md"
                >
                  {getAuthGroupLabel(formData.authGroup)}
                </Badge>
              </Box>
            </VStack>
          </Box>
        </Box>

        {/* Card Image Section */}
        {formData.cardImg && (
          <Box p={4} borderBottom="1px solid" borderColor="gray.100">
            <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
              CARD IMAGE
            </Text>
            {formData.cardImg.startsWith("http") ? (
              <Box
                bg="gray.50"
                p={3}
                borderRadius="md"
                display="inline-flex"
                border="1px solid"
                borderColor="gray.200"
              >
                <Image
                  src={formData.cardImg}
                  alt="Card preview"
                  maxH="100px"
                  objectFit="contain"
                  borderRadius="sm"
                />
              </Box>
            ) : (
              <Text
                fontSize="xs"
                color="gray.700"
                fontFamily="mono"
                bg="gray.50"
                px={2}
                py={1}
                borderRadius="md"
                display="inline-block"
                border="1px solid"
                borderColor="gray.200"
              >
                {formData.cardImg}
              </Text>
            )}
          </Box>
        )}

        {/* Type-Specific Details */}
        {formData.type === "powerbi" && (
          <Box p={4} bg="blue.50">
            <HStack spacing={2} mb={3}>
              <Icon as={ExternalLinkIcon} color="blue.600" boxSize={4} />
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="blue.700"
                textTransform="uppercase"
              >
                Power BI Configuration
              </Text>
            </HStack>

            <VStack align="stretch" spacing={3}>
              {formData.embedURL && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.600"
                    mb={1}
                  >
                    EMBED URL
                  </Text>
                  <Text
                    fontSize="xs"
                    color="blue.700"
                    wordBreak="break-all"
                    fontFamily="mono"
                    bg="white"
                    px={2}
                    py={1.5}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="blue.200"
                  >
                    {formData.embedURL}
                  </Text>
                </Box>
              )}

              <Box
                display="grid"
                gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
                gap={3}
              >
                {formData.workspaceId && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.600"
                      mb={1}
                    >
                      WORKSPACE ID
                    </Text>
                    <Text
                      fontSize="xs"
                      fontFamily="mono"
                      color="gray.700"
                      bg="white"
                      px={2}
                      py={1}
                      borderRadius="md"
                      border="1px solid"
                      borderColor="blue.200"
                    >
                      {formData.workspaceId}
                    </Text>
                  </Box>
                )}

                {formData.reportId && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.600"
                      mb={1}
                    >
                      REPORT ID
                    </Text>
                    <Text
                      fontSize="xs"
                      fontFamily="mono"
                      color="gray.700"
                      bg="white"
                      px={2}
                      py={1}
                      borderRadius="md"
                      border="1px solid"
                      borderColor="blue.200"
                    >
                      {formData.reportId}
                    </Text>
                  </Box>
                )}
              </Box>
            </VStack>
          </Box>
        )}

        {formData.type === "tableau" && formData.embedd && (
          <Box p={4} bg="orange.50">
            <HStack spacing={2} mb={3}>
              <Icon as={ExternalLinkIcon} color="orange.600" boxSize={4} />
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="orange.700"
                textTransform="uppercase"
              >
                Tableau Configuration
              </Text>
            </HStack>

            <Box>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                EMBED URL
              </Text>
              <Text
                fontSize="xs"
                color="orange.700"
                wordBreak="break-all"
                fontFamily="mono"
                bg="white"
                px={2}
                py={1.5}
                borderRadius="md"
                border="1px solid"
                borderColor="orange.200"
              >
                {formData.embedd}
              </Text>
            </Box>
          </Box>
        )}

        {formData.type === "file" && (
          <Box p={4} bg="blue.50">
            <HStack spacing={2} mb={3}>
              <Icon as={AttachmentIcon} color="blue.600" boxSize={4} />
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="blue.700"
                textTransform="uppercase"
              >
                File Storage Details
              </Text>
            </HStack>

            <Box
              display="grid"
              gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
              gap={3}
            >
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  mb={1}
                >
                  BLOB NAME
                </Text>
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  color="gray.700"
                  bg="white"
                  px={2}
                  py={1}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  {formData.blobName}
                </Text>
              </Box>

              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  mb={1}
                >
                  CONTAINER
                </Text>
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  color="gray.700"
                  bg="white"
                  px={2}
                  py={1}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  {formData.container}
                </Text>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </VStack>
  );
};

export default ItemConfirmationStep;
